// "Get ready to take bookings" (design review epic 5): what a new account
// still needs before a client can book her, in the order it's best done.
import type { SettingsPanel } from '../types/app'
import { priceLine } from '../billing/rungs'

export type SetupInput = {
  business: { trading_name: string; address_line1: string | null; city: string | null; published: boolean;
    cover_photo_url: string | null; profile_photo_url: string | null; description: string | null } | null
  services: number
  hours: number
  // noticeChecked: she has read the privacy notice her clients see and
  // said it looks right. Comes from her session, not from this browser.
  noticeChecked: boolean
  cardReady: boolean
  cardShown: boolean
  // cardApproved: on Free, Stripe has approved her and card payments wait
  // for her to turn them on (the £14) on the plan card (review, 2026-10-25).
  cardApproved?: boolean
  // cardStage: Stripe's setup has started, on Stripe's own page; the step
  // says where it stands ('details' still due from her, or Stripe 'checking').
  cardStage?: 'details' | 'checking'
  // hasGoal: she has set a monthly goal (Jessica's feedback, 2026-10-11:
  // goal setting belongs in getting started). Optional, like card payments.
  hasGoal: boolean
  // goalShown: she is on Pro. Off Pro, Goals is a locked screen
  // (2026-10-24), so the step would only open the lock: it is left out.
  goalShown: boolean
}

export type SetupStep = {
  id: 'business' | 'services' | 'hours' | 'notice' | 'page' | 'card' | 'goal'
  title: string
  detail: string
  done: boolean
  optional: boolean
  // Where the step is done: a Settings panel, or the Goals tab. The notice
  // step has no panel either: it opens a sheet of its own.
  panel: SettingsPanel | null
}

// The card step while Stripe has her setup. The title carries it: Today
// lists optional steps compactly, without their detail line.
const CARD_STAGE = {
  none: { title: 'Set up card payments', detail: 'Optional: take cards and deposits.' },
  details: { title: 'Finish card payments on Stripe', detail: 'Your details for Stripe are still due.' },
  checking: { title: 'Stripe is checking your details', detail: 'Usually a few minutes, sometimes a day or two.' },
}

export function setupSteps(x: SetupInput): SetupStep[] {
  const b = x.business
  const looksDone = !!b && (!!b.cover_photo_url || !!b.profile_photo_url) && !!b.description
  const steps: SetupStep[] = [
    { id: 'business', title: 'Add your business details', detail: 'Name and address, so clients know where to come.', done: !!b && !!(b.address_line1 || b.city), optional: false, panel: 'business' },
    { id: 'services', title: 'Add your treatments', detail: 'What clients can book, with lengths and prices.', done: x.services > 0, optional: false, panel: 'services' },
    { id: 'hours', title: 'Set your working hours', detail: 'When clients can book you.', done: x.hours > 0, optional: false, panel: 'hours' },
    { id: 'notice', title: 'Check your privacy notice for clients', detail: 'Harmovi writes it from your details. Clients see it when they book.', done: x.noticeChecked, optional: false, panel: null },
    {
      id: 'page', title: 'Switch your booking page on',
      detail: b?.published && !looksDone ? 'It’s on. Add photos and a few words about you.' : 'Photos, a few words about you, then switch it on.',
      done: !!b?.published, optional: false, panel: 'online-booking',
    },
  ]
  if (x.cardShown) {
    steps.push(
      x.cardApproved
        ? { id: 'card', title: 'Turn on card payments', detail: `Stripe has approved you. ${priceLine('card_payments')}.`, done: x.cardReady, optional: true, panel: 'plan' }
        : { id: 'card', ...CARD_STAGE[x.cardStage ?? 'none'], done: x.cardReady, optional: true, panel: 'card-payments' },
    )
  }
  if (x.goalShown) steps.push({ id: 'goal', title: 'Set your monthly goal', detail: 'Optional: what you want to take home, and the week that gets you there.', done: x.hasGoal, optional: true, panel: null })
  return steps
}

// ready is true once everything a client needs to book is in place.
export const ready = (steps: SetupStep[]) => steps.every((s) => s.done || s.optional)

export interface ChecklistView {
  // The required steps with their count, while any is left ("2 of 5 done"
  // beside exactly those five rows).
  required: SetupStep[]
  done: number
  // The optional steps not yet done, listed apart and labelled Optional; they
  // stay after the required ones are finished, until done or dismissed.
  optional: SetupStep[]
}

// checklistView splits the steps for Today (review 2026-10-14). Null when
// there is nothing left to show.
export function checklistView(steps: SetupStep[], optionalDismissed: boolean): ChecklistView | null {
  const isReady = ready(steps)
  const required = isReady ? [] : steps.filter((s) => !s.optional)
  const optional = isReady && optionalDismissed ? [] : steps.filter((s) => s.optional && !s.done)
  if (required.length === 0 && optional.length === 0) return null
  return { required, done: required.filter((s) => s.done).length, optional }
}
